/**
 * Argus UI kit — the shared visual primitives. Premium "ink + sapphire" look:
 * soft surfaces, hairline borders, restrained shadows, sapphire as the single
 * action accent, semantic compliance tones (clear / at-risk / breach) reserved
 * for meaning. Every page composes these so the product reads as one system.
 */
import { forwardRef, type ButtonHTMLAttributes, type HTMLAttributes, type ReactNode } from "react";
import { cn } from "@/lib/utils";

/* ------------------------------------------------------------------- Brand */

/** The Henosis wordmark (white "HENOSIS" + cyan "O"), tuned for dark surfaces. */
export function HenosisWordmark({ className }: { className?: string }) {
  return <img src="/henosis-wordmark.png" alt="Henosis" className={cn("w-auto", className)} />;
}

/**
 * Product brand lockup: the Henosis wordmark, a hairline divider, and the
 * "Argus" product name — i.e. "Henosis · Argus". Used in the app shell,
 * sign-in, landing, and footer.
 */
export function BrandLockup({
  className,
  size = "md",
}: {
  className?: string;
  size?: "sm" | "md" | "lg";
}) {
  const mark = size === "lg" ? "h-9" : size === "sm" ? "h-6" : "h-7";
  const text = size === "lg" ? "text-2xl" : size === "sm" ? "text-base" : "text-lg";
  return (
    <span className={cn("inline-flex items-center gap-2.5", className)}>
      <HenosisWordmark className={mark} />
      <span aria-hidden className="h-5 w-px bg-neutral-700" />
      <span className={cn("font-semibold tracking-tight text-neutral-50", text)}>Argus</span>
    </span>
  );
}

/* ------------------------------------------------------------------ Button */

type ButtonVariant = "primary" | "secondary" | "ghost" | "danger" | "success";
type ButtonSize = "sm" | "md";

const BUTTON_VARIANTS: Record<ButtonVariant, string> = {
  primary:
    "bg-brand-600 text-white shadow-sm shadow-brand-950/40 hover:bg-brand-500 active:bg-brand-700",
  secondary:
    "border border-neutral-700 bg-neutral-900/60 text-neutral-200 hover:border-neutral-600 hover:bg-neutral-800/80",
  ghost: "text-neutral-300 hover:bg-neutral-800/70 hover:text-neutral-100",
  danger:
    "border border-red-900/70 bg-red-950/30 text-red-300 hover:border-red-800 hover:bg-red-950/60",
  success: "bg-emerald-600 text-emerald-50 hover:bg-emerald-500",
};

const BUTTON_SIZES: Record<ButtonSize, string> = {
  sm: "h-8 gap-1.5 px-2.5 text-xs",
  md: "h-9 gap-2 px-3.5 text-sm",
};

export const Button = forwardRef<
  HTMLButtonElement,
  ButtonHTMLAttributes<HTMLButtonElement> & { variant?: ButtonVariant; size?: ButtonSize }
>(function Button({ className, variant = "secondary", size = "md", ...props }, ref) {
  return (
    <button
      ref={ref}
      className={cn(
        "inline-flex select-none items-center justify-center rounded-md font-medium transition-colors disabled:pointer-events-none disabled:opacity-50",
        BUTTON_VARIANTS[variant],
        BUTTON_SIZES[size],
        className,
      )}
      {...props}
    />
  );
});

/* -------------------------------------------------------------------- Card */

export function Card({ className, ...props }: HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={cn(
        "rounded-xl border border-neutral-800/80 bg-neutral-900/50 shadow-sm shadow-black/20 backdrop-blur-sm",
        className,
      )}
      {...props}
    />
  );
}

export function CardHeader({
  title,
  action,
  className,
}: {
  title: ReactNode;
  action?: ReactNode;
  className?: string;
}) {
  return (
    <div className={cn("flex items-center justify-between gap-3 px-4 pb-2 pt-3.5", className)}>
      <h2 className="text-[11px] font-medium uppercase tracking-wider text-neutral-500">{title}</h2>
      {action}
    </div>
  );
}

/* ------------------------------------------------------------------- Badge */

type Tone = "neutral" | "brand" | "clear" | "risk" | "breach" | "info" | "ai";

const TONE_STYLES: Record<Tone, string> = {
  neutral: "border-neutral-700/70 bg-neutral-800/50 text-neutral-300",
  brand: "border-brand-700/50 bg-brand-950/40 text-brand-300",
  clear: "border-emerald-800/50 bg-emerald-950/40 text-emerald-300",
  risk: "border-amber-800/50 bg-amber-950/40 text-amber-300",
  breach: "border-red-800/60 bg-red-950/50 text-red-300",
  info: "border-sky-800/50 bg-sky-950/40 text-sky-300",
  ai: "border-indigo-800/50 bg-indigo-950/40 text-indigo-300",
};

export function Badge({
  tone = "neutral",
  className,
  children,
}: {
  tone?: Tone;
  className?: string;
  children: ReactNode;
}) {
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-medium",
        TONE_STYLES[tone],
        className,
      )}
    >
      {children}
    </span>
  );
}

/** A small colored status dot, for inline state cues paired with a label. */
export function Dot({ tone = "neutral", className }: { tone?: Tone; className?: string }) {
  const color: Record<Tone, string> = {
    neutral: "bg-neutral-500",
    brand: "bg-brand-500",
    clear: "bg-emerald-500",
    risk: "bg-amber-500",
    breach: "bg-red-500",
    info: "bg-sky-500",
    ai: "bg-indigo-500",
  };
  return <span className={cn("inline-block size-1.5 rounded-full", color[tone], className)} />;
}

/* ------------------------------------------------------------- PageHeader */

export function PageHeader({
  title,
  subtitle,
  actions,
}: {
  title: ReactNode;
  subtitle?: ReactNode;
  actions?: ReactNode;
}) {
  return (
    <header className="flex flex-wrap items-end justify-between gap-4">
      <div className="space-y-1">
        <h1 className="text-2xl font-semibold tracking-tight text-neutral-50">{title}</h1>
        {subtitle && <p className="text-sm text-neutral-400">{subtitle}</p>}
      </div>
      {actions && <div className="flex items-center gap-2">{actions}</div>}
    </header>
  );
}

/* ------------------------------------------------------------------ Field */

/** Labeled form field — nests the control in the <label> for accessibility. */
export function Field({
  label,
  children,
  className,
}: {
  label: ReactNode;
  children: ReactNode;
  className?: string;
}) {
  return (
    <label className={cn("block space-y-1", className)}>
      <span className="block text-[11px] font-medium uppercase tracking-wider text-neutral-500">
        {label}
      </span>
      {children}
    </label>
  );
}

/* ------------------------------------------------------------- EmptyState */

export function EmptyState({
  icon,
  title,
  description,
  action,
}: {
  icon?: ReactNode;
  title: string;
  description?: ReactNode;
  action?: ReactNode;
}) {
  return (
    <div className="flex flex-col items-center justify-center gap-3 rounded-xl border border-dashed border-neutral-800 bg-neutral-900/30 px-6 py-14 text-center">
      {icon && <div className="text-neutral-600">{icon}</div>}
      <div className="space-y-1">
        <p className="text-sm font-medium text-neutral-300">{title}</p>
        {description && <p className="mx-auto max-w-sm text-xs text-neutral-500">{description}</p>}
      </div>
      {action}
    </div>
  );
}

/* ---------------------------------------------------------------- Spinner */

export function Spinner({ className }: { className?: string }) {
  return (
    <svg
      className={cn("size-4 animate-spin text-neutral-400", className)}
      viewBox="0 0 24 24"
      fill="none"
    >
      <circle cx="12" cy="12" r="10" stroke="currentColor" strokeOpacity="0.25" strokeWidth="3" />
      <path
        d="M12 2a10 10 0 0 1 10 10"
        stroke="currentColor"
        strokeWidth="3"
        strokeLinecap="round"
      />
    </svg>
  );
}

/* ------------------------------------------------------------ Confidence */

/** Confidence chip — labeled High/Med/Low + %, never a bare number (trust UX). */
export function ConfidenceChip({ value, className = "" }: { value: number; className?: string }) {
  const pct = Math.round(value * 100);
  const { tone, label } =
    value >= 0.85
      ? { tone: "clear" as Tone, label: "High" }
      : value >= 0.6
        ? { tone: "risk" as Tone, label: "Med" }
        : { tone: "breach" as Tone, label: "Low" };
  return (
    <Badge tone={tone} className={className}>
      <Dot tone={tone} /> {label} · {pct}%
    </Badge>
  );
}
